import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  FileText, Clock, CheckCircle, XCircle, Warning,
  TrendUp, CurrencyDollar, Users, CalendarBlank, ChartBar, ArrowRight
} from '@phosphor-icons/react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const STATUS_COLORS = {
  'Draft': 'bg-[#F1F5F9] text-[#475569]',
  'Pending Supervisor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Submitted': 'bg-[#DBEAFE] text-[#1E40AF]',
  'Assigned': 'bg-[#DBEAFE] text-[#1E40AF]',
  'Pending AP Processor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending AP Supervisor Review': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending Accounting Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Pending General Manager Approval': 'bg-[#FEF9C3] text-[#854D0E]',
  'Approved': 'bg-[#DCFCE7] text-[#166534]',
  'Returned to Requestor': 'bg-[#FFEDD5] text-[#9A3412]',
  'Rejected': 'bg-[#FEE2E2] text-[#991B1B]',
  'Cancelled': 'bg-[#F1F5F9] text-[#475569]'
};
const PIE_COLORS = ['#0055FF','#22C55E','#EAB308','#EF4444','#8B5CF6','#EC4899','#06B6D4','#F97316','#6366F1','#14B8A6'];

const AM = 'Pending Accounting Manager Approval';
const GM = 'Pending General Manager Approval';

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(n || 0);

// Every drill-down is a link to the requests list carrying the same filters the
// figure was computed from, so the list that opens shows the number that was
// clicked. Date windows come from the SERVER (month_start / year_start /
// aging_ranges): worked out here they would use the browser's clock, and a row
// near a bucket edge would land in a different bucket than the one counted.
const drill = (params) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, v);
  });
  return `/payments?${p.toString()}`;
};

const pad = (n) => String(n).padStart(2, '0');
const yearOf = (iso) => (iso ? parseInt(iso.slice(0, 4), 10) : new Date().getFullYear());

// A calendar month in the year the board is reporting on.
const monthWindow = (monthIdx, yearStart) => {
  const y = yearOf(yearStart);
  const lastDay = new Date(Date.UTC(y, monthIdx + 1, 0)).getUTCDate();
  return {
    date_from: `${y}-${pad(monthIdx + 1)}-01`,
    date_to: `${y}-${pad(monthIdx + 1)}-${pad(lastDay)}T23:59:59+00:00`,
  };
};

// Recharts hands the datum back differently per chart type and version.
const datum = (d) => (d && d.payload) || d || {};

function StatCard({ title, value, icon: Icon, color = '#0055FF', subtitle, to, testId }) {
  const inner = (
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">{title}</p>
          <p className="mt-1 text-2xl font-bold tracking-tight" style={{ color }}>{value}</p>
          {subtitle && <p className="mt-0.5 text-xs text-[#94A3B8]">{subtitle}</p>}
          {to && (
            <p className="mt-1 text-[10px] uppercase tracking-[0.1em] font-medium text-[#94A3B8] group-hover:text-[#0055FF] transition-colors flex items-center gap-1">
              View requests <ArrowRight size={10} />
            </p>
          )}
        </div>
        <div className="p-2 rounded-sm" style={{ backgroundColor: `${color}10` }}>
          <Icon size={20} weight="duotone" style={{ color }} />
        </div>
      </div>
    </CardContent>
  );

  if (!to) return <Card className="rounded-sm border border-border">{inner}</Card>;

  return (
    <Link to={to} className="block group rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]" data-testid={testId}>
      <Card className="rounded-sm border border-border h-full transition-colors group-hover:border-[#0055FF] group-hover:bg-[#F8FAFC]">
        {inner}
      </Card>
    </Link>
  );
}

// A number in a box that opens the rows behind it.
function DrillTile({ value, label, to, tone = 'default', testId }) {
  const tones = {
    default: 'border bg-white text-[#0F172A]',
    pending: 'border bg-[#FEF9C3]/20 text-[#854D0E]',
  };
  return (
    <Link
      to={to}
      className={`group block text-center p-3 rounded-sm transition-colors hover:border-[#0055FF] hover:bg-[#F8FAFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF] ${tones[tone]}`}
      data-testid={testId}
    >
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-[#475569] mt-1">{label}</p>
    </Link>
  );
}

// The rows behind a chart, as links. A chart segment is never the only way to
// drill: a pie slice is a poor tap target on a 375px screen, and a chart that
// fails to paint takes its click handler with it.
function DrillList({ rows, labelKey, to, testPrefix }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-3 space-y-0.5 border-t pt-2">
      {rows.map((r, i) => (
        <Link key={i} to={to(r)}
              className="flex items-center justify-between gap-2 min-h-[44px] py-2 px-2 -mx-2 sm:min-h-0 sm:py-1 sm:px-1 sm:-mx-1 rounded-sm hover:bg-[#F8FAFC] transition-colors group"
              data-testid={`${testPrefix}-${i}`}>
          <span className="text-xs text-[#0F172A] truncate flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
            {r[labelKey]}
          </span>
          <span className="text-xs font-medium text-[#475569] group-hover:text-[#0055FF] whitespace-nowrap">{fmt(r.amount)}</span>
        </Link>
      ))}
    </div>
  );
}

// Says the chart is clickable. A drill-down nobody knows about is not one.
function DrillHint({ children = 'Click to open the requests behind a figure' }) {
  return <span className="text-[10px] font-normal normal-case tracking-normal text-[#94A3B8]">{children}</span>;
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.05em] font-semibold rounded-sm ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// AP Accounting Dashboard
function APDashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get('/dashboard/ap').then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <DashboardSkeleton />;

  // Every AP figure counts requests this user SUBMITTED, which is a different
  // set from the list's default view (their assigned queue plus AP intake) --
  // so each drill carries submitted_by, or the list would open on a different
  // number than the tile.
  const mine = { submitted_by: user?.id };

  return (
    <div className="space-y-6" data-testid="ap-dashboard">
      <h1 className="font-heading text-3xl font-bold tracking-tight">AP Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard title="Submitted This Month" value={data.total_this_month} icon={FileText}
                  to={drill({ ...mine, date_from: data.month_start })} testId="drill-ap-this-month" />
        <StatCard title="Pending Approval"
                  value={(data.by_status?.[AM] || 0) + (data.by_status?.[GM] || 0)}
                  icon={Clock} color="#EAB308"
                  to={drill({ ...mine, status: `${AM},${GM}` })} testId="drill-ap-pending" />
        <StatCard title="Approved" value={data.by_status?.Approved || 0} icon={CheckCircle} color="#22C55E"
                  to={drill({ ...mine, status: 'Approved' })} testId="drill-ap-approved" />
        <StatCard title="Rejected" value={data.by_status?.Rejected || 0} icon={XCircle} color="#EF4444"
                  to={drill({ ...mine, status: 'Rejected' })} testId="drill-ap-rejected" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Recent Requests</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Amount</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.recent_requests?.slice(0, 8).map(r => (
                  <TableRow key={r.id}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] hover:underline text-sm">{r.request_code}</Link></TableCell><TableCell className="text-right text-sm">{fmt(r.amount)}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Top Vendors</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.top_vendors?.slice(0, 6).map((v, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-border last:border-0">
                  <span className="text-sm text-[#0F172A]">{v.name}</span>
                  <span className="text-sm font-medium text-right">{fmt(v.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {data.upcoming_due?.length > 0 && (
        <Card className="rounded-sm border border-[#EAB308]/30 bg-[#FEF9C3]/20">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Warning size={16} className="text-[#EAB308]" />Upcoming Due Dates</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Vendor</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Due</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.upcoming_due?.map(r => (
                  <TableRow key={r.id}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] text-sm">{r.request_code}</Link></TableCell><TableCell className="text-sm">{r.vendor_name}</TableCell><TableCell className="text-sm">{r.due_date}</TableCell><TableCell className="text-right text-sm">{fmt(r.amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Accounting Manager Dashboard
function AcctManagerDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/dashboard/accounting-manager').then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <DashboardSkeleton />;

  const monthlyData = data.monthly_trend?.map(m => ({ name: MONTHS[m.month - 1], amount: m.amount })) || [];
  const ytd = { status: 'Approved', date_from: data.year_start };

  return (
    <div className="space-y-6" data-testid="acct-manager-dashboard">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Accounting Manager Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard title="Pending Approvals" value={data.pending_count} icon={Clock} color="#EAB308"
                  to={drill({ status: AM })} testId="drill-am-pending" />
        <StatCard title="Pending Amount" value={fmt(data.pending_amount)} icon={CurrencyDollar} color="#EAB308"
                  to={drill({ status: AM })} testId="drill-am-pending-amount" />
        <StatCard title="YTD Expenses" value={fmt(data.ytd_expenses)} icon={TrendUp} color="#0055FF"
                  to={drill(ytd)} testId="drill-am-ytd" />
        {/* Projected Annual is an estimate, not a set of rows -- deliberately
            not drillable, since there is nothing truthful to open. */}
        <StatCard title="Projected Annual" value={fmt(data.projected_annual)} icon={CalendarBlank} color="#22C55E" subtitle="Estimate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-sm border lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Monthly Approved Expenses <DrillHint>Click a month</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={monthlyData} style={{ cursor: 'pointer' }}
                         onClick={(e) => { const i = MONTHS.indexOf(e?.activeLabel); if (i >= 0) navigate(drill({ status: 'Approved', ...monthWindow(i, data.year_start) })); }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Area type="monotone" dataKey="amount" stroke="#0055FF" fill="#0055FF" fillOpacity={0.1} /></AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">By Category <DrillHint>Click a slice or a row</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={data.category_breakdown?.slice(0, 8)} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80} cursor="pointer"
                             onClick={(d) => { const c = datum(d).category; if (c) navigate(drill({ ...ytd, category: c })); }}
                             label={({ category }) => category?.length > 12 ? category.slice(0,12)+'...' : category}>
                {data.category_breakdown?.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie><Tooltip formatter={v => fmt(v)} /></PieChart>
            </ResponsiveContainer>
            <DrillList rows={data.category_breakdown?.slice(0, 8)} labelKey="category"
                       to={(r) => drill({ ...ytd, category: r.category })} testPrefix="drill-am-category" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border"><CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-sm font-semibold">Pending Approvals</CardTitle><Link to={drill({ status: AM })} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1 min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0" data-testid="drill-am-pending-all">See all {data.pending_count} <ArrowRight size={11} /></Link></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Vendor</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.pending_approvals?.slice(0, 8).map(r => (
                  <TableRow key={r.id}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] hover:underline text-sm">{r.request_code}</Link></TableCell><TableCell className="text-sm">{r.vendor_name}</TableCell><TableCell className="text-right text-sm font-medium">{fmt(r.amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Top Vendors by Spend <DrillHint>Click a bar or a row</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.top_vendors?.slice(0, 6)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} /><Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="amount" fill="#0055FF" radius={[0, 2, 2, 0]} cursor="pointer"
                     onClick={(d) => { const v = datum(d).id; if (v) navigate(drill({ ...ytd, vendor_id: v })); }} />
              </BarChart>
            </ResponsiveContainer>
            <DrillList rows={data.top_vendors?.slice(0, 6)} labelKey="name"
                       to={(r) => drill({ ...ytd, vendor_id: r.id })} testPrefix="drill-am-vendor" />
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Approval Aging <DrillHint>Click a bucket</DrillHint></CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(data.aging || {}).map(([k, v]) => (
              <DrillTile key={k} value={v} label={k}
                         to={drill({ status: AM, ...(data.aging_ranges?.[k] || {}) })}
                         testId={`drill-am-aging-${k.replace(/[^a-z0-9]+/gi, '-')}`} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// General Manager Dashboard
function GMDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/dashboard/general-manager').then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <DashboardSkeleton />;

  const monthlyData = data.monthly_trend?.map(m => ({ name: MONTHS[m.month - 1], amount: m.amount })) || [];
  const ytd = { status: 'Approved', date_from: data.year_start };

  return (
    <div className="space-y-6" data-testid="gm-dashboard">
      <h1 className="font-heading text-3xl font-bold tracking-tight">General Manager Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard title="Pending Final Approval" value={data.pending_count} icon={Clock} color="#EAB308"
                  to={drill({ status: GM })} testId="drill-gm-pending" />
        <StatCard title="Pending Amount" value={fmt(data.pending_amount)} icon={CurrencyDollar} color="#EAB308"
                  to={drill({ status: GM })} testId="drill-gm-pending-amount" />
        <StatCard title="YTD Expenses" value={fmt(data.ytd_expenses)} icon={TrendUp} color="#0055FF"
                  to={drill(ytd)} testId="drill-gm-ytd" />
        <StatCard title="Projected Annual" value={fmt(data.projected_annual)} icon={CalendarBlank} color="#22C55E" subtitle="Estimate" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-sm border lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Monthly Spend Trend <DrillHint>Click a month</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={monthlyData} style={{ cursor: 'pointer' }}
                         onClick={(e) => { const i = MONTHS.indexOf(e?.activeLabel); if (i >= 0) navigate(drill({ status: 'Approved', ...monthWindow(i, data.year_start) })); }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Area type="monotone" dataKey="amount" stroke="#0055FF" fill="#0055FF" fillOpacity={0.1} /></AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Category Summary <DrillHint>Click a row</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {data.category_summary?.slice(0, 8).map((c, i) => (
                <Link key={i} to={drill({ ...ytd, category: c.category })}
                      className="flex items-center justify-between min-h-[44px] py-2 px-2 -mx-2 sm:min-h-0 sm:py-1 sm:px-1 sm:-mx-1 rounded-sm border-b last:border-0 hover:bg-[#F8FAFC] transition-colors"
                      data-testid={`drill-gm-category-${i}`}>
                  <span className="text-sm">{c.category}</span>
                  <span className="text-sm font-medium text-right">{fmt(c.amount)}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border"><CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-sm font-semibold">Pending Final Approvals</CardTitle><Link to={drill({ status: GM })} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1 min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0" data-testid="drill-gm-pending-all">See all {data.pending_count} <ArrowRight size={11} /></Link></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Vendor</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.pending_approvals?.slice(0, 8).map(r => (
                  <TableRow key={r.id}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] hover:underline text-sm">{r.request_code}</Link></TableCell><TableCell className="text-sm">{r.vendor_name}</TableCell><TableCell className="text-right text-sm font-medium">{fmt(r.amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {data.high_value_requests?.length > 0 && (
          <Card className="rounded-sm border border-[#EF4444]/20"><CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Warning size={16} className="text-[#EF4444]" />High Value Requests</CardTitle><Link to={drill({ status: GM, amount_min: data.high_value_threshold || 50000 })} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1 min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0" data-testid="drill-gm-high-value">See all <ArrowRight size={11} /></Link></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Vendor</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.high_value_requests?.slice(0, 5).map(r => (
                    <TableRow key={r.id}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] text-sm">{r.request_code}</Link></TableCell><TableCell className="text-sm">{r.vendor_name}</TableCell><TableCell className="text-right text-sm font-bold text-[#991B1B]">{fmt(r.amount)}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Owner Dashboard
function OwnerDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get('/dashboard/owner').then(r => setData(r.data)).catch(console.error); }, []);
  if (!data) return <DashboardSkeleton />;

  const monthlyData = data.monthly_trend?.map(m => ({ name: MONTHS[m.month - 1], amount: m.amount })) || [];
  const ytd = { status: 'Approved', date_from: data.year_start };
  const lastYear = yearOf(data.year_start) - 1;

  return (
    <div className="space-y-6" data-testid="owner-dashboard">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Executive Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard title="YTD Expenses" value={fmt(data.ytd_expenses)} icon={CurrencyDollar} color="#0055FF"
                  to={drill(ytd)} testId="drill-owner-ytd" />
        <StatCard title="Projected Annual" value={fmt(data.projected_annual)} icon={TrendUp} color="#22C55E" subtitle="Estimate" />
        {/* The subtitle names last year's total, so the drill opens last year. */}
        <StatCard title="YoY Change" value={`${data.yoy_change > 0 ? '+' : ''}${data.yoy_change}%`} icon={ChartBar}
                  color={data.yoy_change > 0 ? '#EF4444' : '#22C55E'} subtitle={`vs ${fmt(data.last_year_total)} last year`}
                  to={drill({ status: 'Approved', date_from: `${lastYear}-01-01`, date_to: `${lastYear}-12-31T23:59:59+00:00` })}
                  testId="drill-owner-lastyear" />
        {/* Turnaround is measured across approval events, not a set of rows. */}
        <StatCard title="Avg Turnaround" value={`${data.avg_turnaround_hours}h`} icon={Clock} color="#8B5CF6" subtitle="Approval time" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="rounded-sm border lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Monthly Expense Trend <DrillHint>Click a month</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={monthlyData} style={{ cursor: 'pointer' }}
                         onClick={(e) => { const i = MONTHS.indexOf(e?.activeLabel); if (i >= 0) navigate(drill({ status: 'Approved', ...monthWindow(i, data.year_start) })); }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => fmt(v)} /><Area type="monotone" dataKey="amount" stroke="#0055FF" fill="#0055FF" fillOpacity={0.1} /></AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Expense by Category <DrillHint>Click a slice or a row</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart><Pie data={data.category_breakdown?.slice(0, 8)} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={85} cursor="pointer"
                             onClick={(d) => { const c = datum(d).category; if (c) navigate(drill({ ...ytd, category: c })); }}
                             label={({ category }) => category?.length > 10 ? category.slice(0,10)+'...' : category}>
                {data.category_breakdown?.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie><Tooltip formatter={v => fmt(v)} /></PieChart>
            </ResponsiveContainer>
            <DrillList rows={data.category_breakdown?.slice(0, 8)} labelKey="category"
                       to={(r) => drill({ ...ytd, category: r.category })} testPrefix="drill-owner-category" />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Top 10 Vendors <DrillHint>Click a bar or a row</DrillHint></CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.top_vendors?.slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `₱${(v/1000).toFixed(0)}k`} /><YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} /><Tooltip formatter={v => fmt(v)} />
                <Bar dataKey="amount" fill="#0055FF" radius={[0, 2, 2, 0]} cursor="pointer"
                     onClick={(d) => { const v = datum(d).id; if (v) navigate(drill({ ...ytd, vendor_id: v })); }} />
              </BarChart>
            </ResponsiveContainer>
            <DrillList rows={data.top_vendors?.slice(0, 10)} labelKey="name"
                       to={(r) => drill({ ...ytd, vendor_id: r.id })} testPrefix="drill-owner-vendor" />
          </CardContent>
        </Card>

        <Card className="rounded-sm border"><CardHeader className="pb-2 flex-row items-center justify-between space-y-0"><CardTitle className="text-sm font-semibold">Largest Requests</CardTitle><Link to={drill({ status: 'Approved' })} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1 min-h-[44px] sm:min-h-0 px-2 -mx-2 sm:px-0 sm:mx-0" data-testid="drill-owner-approved">All approved <ArrowRight size={11} /></Link></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">Code</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Category</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em] text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.largest_requests?.slice(0, 8).map(r => (
                  <TableRow key={r.id || r.request_code}><TableCell><Link to={`/payments/${r.id}`} className="text-[#0055FF] text-sm">{r.request_code}</Link></TableCell><TableCell className="text-sm">{r.category}</TableCell><TableCell className="text-right text-sm font-bold">{fmt(r.amount)}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm border"><CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">Pending by Stage <DrillHint>Click a stage</DrillHint></CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(data.pending_by_stage || {}).map(([k, v]) => (
              <DrillTile key={k} value={v} label={k} tone="pending" to={drill({ status: k })}
                         testId={`drill-owner-stage-${k.replace(/[^a-z0-9]+/gi, '-')}`} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-10 w-64 bg-muted rounded-sm" />
      <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted rounded-sm" />)}</div>
      <div className="grid grid-cols-2 gap-4">{[1,2].map(i => <div key={i} className="h-64 bg-muted rounded-sm" />)}</div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  if (!user) return null;

  switch (user.role) {
    case 'ap_accounting': return <APDashboard />;
    case 'accounting_manager': return <AcctManagerDashboard />;
    case 'general_manager': return <GMDashboard />;
    case 'owner': return <OwnerDashboard />;
    default: return <div>Unknown role</div>;
  }
}
